import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { generateAssignmentPDF } from '../../utils/pdfGenerator';
import AssignmentSubmission from '../../components/student/AssignmentSubmission';
import QuizTaker from '../../components/student/QuizTaker';
import {
    ClipboardDocumentListIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    BookOpenIcon,
    FunnelIcon,
    DocumentTextIcon,
    ClockIcon,
    CheckCircleIcon,
    PencilSquareIcon
} from '@heroicons/react/24/outline';

const StudentGlobalTasks = () => {
    const { courses } = useAppState();
    const navigate = useNavigate();
    const [selectedCourseId, setSelectedCourseId] = useState('all');
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);

    // Modal states
    const [submittingAssignment, setSubmittingAssignment] = useState(null);
    const [takingQuiz, setTakingQuiz] = useState(null);

    // Aggregate all tasks
    const allTasks = useMemo(() => {
        const tasks = [];
        courses.forEach(course => {
            // Normalize submissions access
            // LearningAreaSerializer uses 'student_submissions.assignments'
            // CourseDetailSerializer uses 'assignment_submissions'
            const assignmentSubmissions = course.student_submissions?.assignments || course.assignment_submissions || [];
            const quizSubmissions = course.student_submissions?.quizzes || course.quiz_submissions || [];

            // Assignments
            (course.assignments || []).forEach(asgn => {
                const submission = assignmentSubmissions.find(s => s.assignment === asgn.id);
                tasks.push({
                    ...asgn,
                    type: 'assignment',
                    courseName: course.name,
                    courseCode: course.code,
                    courseId: course.id,
                    submission,
                    isSubmitted: !!submission, // If submission exists, it's submitted
                    isGraded: submission?.status === 'graded',
                    dueDate: new Date(asgn.due_date),
                    originalObject: asgn
                });
            });

            // Quizzes
            (course.quizzes || []).forEach(quiz => {
                const submissions = quizSubmissions.filter(s => s.quiz === quiz.id);
                const attemptsUsed = submissions.length;
                const isLimitReached = attemptsUsed >= (quiz.max_attempts || 1);
                const highestScore = submissions.length > 0 ? Math.max(...submissions.map(s => s.score)) : null;

                tasks.push({
                    ...quiz,
                    type: 'quiz',
                    courseName: course.name,
                    courseCode: course.code,
                    courseId: course.id,
                    submissions,
                    attemptsUsed,
                    isLimitReached,
                    highestScore,
                    dueDate: quiz.due_date ? new Date(quiz.due_date) : null,
                    // "New" logic: 0 attempts
                    isNew: attemptsUsed === 0
                });
            });
        });

        // Sort by due date (assignments) or created date?
        // Let's sort by "New/Pending" first, then Date
        return tasks.sort((a, b) => {
            // Prioritize unsubmitted assignments and unattempted quizzes
            const aPending = (a.type === 'assignment' && !a.isSubmitted) || (a.type === 'quiz' && a.isNew);
            const bPending = (b.type === 'assignment' && !b.isSubmitted) || (b.type === 'quiz' && b.isNew);

            if (aPending && !bPending) return -1;
            if (!aPending && bPending) return 1;

            // Then by due date if available
            if (a.dueDate && b.dueDate) return a.dueDate - b.dueDate;
            if (a.dueDate) return -1;
            if (b.dueDate) return 1;

            return 0;
        });
    }, [courses]);

    const filteredTasks = useMemo(() => {
        if (selectedCourseId === 'all') return allTasks;
        return allTasks.filter(t => t.courseId === selectedCourseId);
    }, [allTasks, selectedCourseId]);

    const activeCourseName = selectedCourseId === 'all'
        ? 'All Learning Areas'
        : courses.find(c => c.id === selectedCourseId)?.name || 'Unknown Course';

    // Calculate stats for header
    const totalPending = allTasks.filter(t => (t.type === 'assignment' && !t.isSubmitted) || (t.type === 'quiz' && t.isNew)).length;
    const totalCompleted = allTasks.length - totalPending;

    return (
        <div className="min-h-screen bg-slate-50 pb-24 md:pb-10">
            <main className="max-w-7xl mx-auto py-6 md:py-10 px-4 sm:px-6 lg:px-8 space-y-6 md:space-y-10">

                {/* Desktop Header */}
                <header className="hidden md:block relative overflow-hidden bg-[#18216D] rounded-[2.5rem] p-12 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">My Workload</p>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter italic">Global Tasks</h1>
                        <p className="text-indigo-100/70 max-w-2xl font-medium text-lg mt-6">
                            View and manage all your assignments and quizzes from one place.
                        </p>
                    </div>
                </header>

                {/* Mobile Header (Redesigned) */}
                <div className="md:hidden">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">My Workload</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter mb-6">Global Tasks</h1>

                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 bg-white p-3 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Todo</div>
                            <div className="text-lg font-black text-[#18216D]">{totalPending}</div>
                        </div>
                        <div className="flex-1 bg-[#18216D] p-3 rounded-2xl border border-[#18216D] flex items-center justify-between shadow-lg shadow-indigo-900/20">
                            <div className="text-[10px] font-black uppercase tracking-widest text-[#FFC425]">Done</div>
                            <div className="text-lg font-black text-white">{totalCompleted}</div>
                        </div>
                    </div>
                </div>

                {/* Filter Accordion */}
                <section className="bg-transparent md:bg-white md:rounded-3xl md:p-6 md:shadow-sm md:border md:border-slate-100">
                    <div className="hidden md:flex items-center gap-4 mb-6">
                        <div className="h-10 w-10 bg-indigo-50 text-[#18216D] rounded-xl flex items-center justify-center">
                            <FunnelIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight">Filter Tasks</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Select a learning area to focus</p>
                        </div>
                    </div>

                    <div className="relative">
                        {/* Mobile Trigger */}
                        <button
                            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                            className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 font-black text-[#18216D] uppercase tracking-widest text-xs hover:shadow-md transition-all md:hidden"
                        >
                            <span className="truncate pr-4">{activeCourseName}</span>
                            {isAccordionOpen ? <ChevronUpIcon className="w-4 h-4 shrink-0" /> : <ChevronDownIcon className="w-4 h-4 shrink-0" />}
                        </button>

                        {/* Filter List */}
                        <div className={`flex flex-wrap gap-3 mt-4 md:mt-0 ${isAccordionOpen ? 'block' : 'hidden md:flex'}`}>
                            <button
                                onClick={() => { setSelectedCourseId('all'); setIsAccordionOpen(false); }}
                                className={`w-full md:w-auto px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border-2 text-left md:text-center ${selectedCourseId === 'all'
                                    ? 'bg-[#18216D] text-white border-[#18216D] shadow-lg shadow-indigo-900/20 md:scale-105'
                                    : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                            >
                                All Learning Areas
                            </button>
                            {courses.map(course => (
                                <button
                                    key={course.id}
                                    onClick={() => { setSelectedCourseId(course.id); setIsAccordionOpen(false); }}
                                    className={`w-full md:w-auto px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border-2 text-left md:text-center ${selectedCourseId === course.id
                                        ? 'bg-[#18216D] text-white border-[#18216D] shadow-lg shadow-indigo-900/20 md:scale-105'
                                        : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                                >
                                    {course.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Task Grid */}
                {filteredTasks.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTasks.map((task, idx) => (
                            <div key={`${task.type}-${task.id}`} className="bg-white rounded-3xl border border-gray-100 p-6 hover:shadow-xl hover:shadow-indigo-900/5 transition-all group flex flex-col h-full relative overflow-hidden">
                                {/* Course Label */}
                                <div className="absolute top-0 right-0 bg-slate-50 px-4 py-2 rounded-bl-2xl border-b border-l border-slate-100">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{task.courseName}</span>
                                </div>

                                <div className="flex-1 space-y-4 pt-4">
                                    <div className="flex justify-between items-start pr-12">
                                        <div>
                                            <div className="flex items-center space-x-2 mb-2">
                                                <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded ${task.type === 'assignment' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                                                    {task.type === 'assignment' ? 'Task' : 'Quiz'}
                                                </span>
                                                {/* Status Badges */}
                                                {task.type === 'assignment' && task.isSubmitted && (
                                                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded flex items-center gap-1">
                                                        <CheckCircleIcon className="w-3 h-3" /> Submitted
                                                    </span>
                                                )}
                                                {task.type === 'quiz' && task.isLimitReached && (
                                                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded flex items-center gap-1">
                                                        Completed
                                                    </span>
                                                )}
                                                {/* NEW Badge */}
                                                {((task.type === 'assignment' && !task.isSubmitted) || (task.type === 'quiz' && task.isNew)) && (
                                                    <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-widest rounded animate-pulse">
                                                        New
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-xl font-black text-[#18216D] leading-tight line-clamp-2">{task.title}</h3>
                                        </div>

                                        {/* OPTIONAL: Grade/Score Badge */}
                                        {task.type === 'assignment' && task.isGraded && (
                                            <div className="bg-[#FFC425] px-3 py-1.5 rounded-xl shadow-lg shadow-yellow-500/10 border border-[#FFC425]/20 text-center animate-in zoom-in duration-300 ml-2 shrink-0">
                                                <div className="text-[8px] font-black text-[#18216D]/60 uppercase tracking-widest leading-none mb-1">Grade</div>
                                                <div className="text-sm font-black text-[#18216D] leading-none">
                                                    {task.submission.competency_level || task.submission.grade || (task.submission.score != null ? `${task.submission.score} pts` : 'Grades Pending')}
                                                </div>
                                            </div>
                                        )}
                                        {task.type === 'quiz' && task.highestScore !== null && (
                                            <div className="bg-[#18216D] px-3 py-1.5 rounded-xl shadow-lg shadow-indigo-900/10 border border-white/10 text-center animate-in zoom-in duration-300 ml-2 shrink-0">
                                                <div className="text-[7px] font-black text-indigo-200 uppercase tracking-widest leading-none mb-1">Score</div>
                                                <div className="text-sm font-black text-white leading-none">
                                                    {task.highestScore}/{task.total_points || 0}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Assignment Specifics */}
                                    {task.type === 'assignment' && (
                                        <>
                                            <p className="text-sm text-gray-500 mt-2 line-clamp-2 leading-relaxed">{task.description}</p>
                                            <div className="flex items-center gap-6 pt-2">
                                                <div className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-wider">
                                                    <span className="text-[#FFC425]"><ClockIcon className="w-4 h-4" /></span>
                                                    {task.dueDate.toLocaleDateString()}
                                                </div>
                                                <div className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-wider">
                                                    <span className="text-[#18216D]"><DocumentTextIcon className="w-4 h-4" /></span>
                                                    {task.total_marks} Pts
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Quiz Specifics */}
                                    {task.type === 'quiz' && (
                                        <div className="space-y-3 mt-2">
                                            <div className="flex items-center gap-6">
                                                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    <ClockIcon className="w-4 h-4 text-[#FFC425]" />
                                                    {task.dueDate ? task.dueDate.toLocaleDateString() : 'No Deadline'}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    <DocumentTextIcon className="w-4 h-4 text-[#18216D]" />
                                                    {task.total_points || (task.questions?.length || 0)} Pts
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                <div className="flex items-center gap-1.5">
                                                    <ClockIcon className="w-4 h-4 text-slate-300" />
                                                    {task.time_limit_minutes} Mins
                                                </div>
                                                <div className="flex items-center gap-1.5 text-indigo-400">
                                                    <BookOpenIcon className="w-4 h-4" />
                                                    {task.attemptsUsed} / {task.max_attempts || 1} Attempts
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="pt-6 mt-auto">
                                    {task.type === 'assignment' ? (
                                        <div className="flex items-center gap-3">
                                            {task.is_manual ? (
                                                <div className="flex-1 px-4 py-3 bg-[#FFC425]/10 border border-[#FFC425]/30 rounded-xl flex items-center justify-center gap-2">
                                                    <span className="text-[9px] font-black text-[#18216D] uppercase tracking-widest text-center">
                                                        Manual Task - No digital submission required
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex-1 flex gap-2">
                                                    {task.isGraded && (
                                                        <button
                                                            onClick={() => navigate(`/student/assignments/${task.id}/feedback/${task.submission.id}`)}
                                                            className="p-3 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-xl transition-all shadow-sm flex items-center gap-2"
                                                            title="View Detailed Feedback"
                                                        >
                                                            <PencilSquareIcon className="w-5 h-5" />
                                                            <span className="hidden sm:inline text-[10px] font-black uppercase tracking-widest">Feedback</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setSubmittingAssignment({ ...task.originalObject, existing_submission: task.submission })}
                                                        disabled={task.isGraded}
                                                        className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${task.isGraded
                                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                                                            : 'bg-[#18216D] text-white hover:bg-[#0D164F] shadow-indigo-900/10'}`}
                                                    >
                                                        {task.isGraded ? 'Work Handed In' : task.isSubmitted ? 'Update Your Work' : 'Submit Work'}
                                                    </button>
                                                </div>
                                            )}
                                            <button
                                                onClick={() => generateAssignmentPDF(task.originalObject)}
                                                className="p-3 bg-slate-50 text-slate-400 hover:text-[#18216D] rounded-xl transition-all"
                                                title="Download PDF"
                                            >
                                                <i className="fas fa-download" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => !task.isLimitReached && setTakingQuiz(task)}
                                            disabled={task.isLimitReached}
                                            className={`w-full py-4 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all shadow-sm ${task.isLimitReached
                                                ? 'bg-[#FFC425] text-[#18216D] hover:bg-[#E5B022]'
                                                : 'bg-slate-50 text-[#18216D] group-hover:bg-[#18216D] group-hover:text-white'}`}
                                        >
                                            {task.isLimitReached ? 'Review Result' : 'Start Assessment'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100">
                        <div className="h-20 w-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300">
                            <ClipboardDocumentListIcon className="w-10 h-10" />
                        </div>
                        <h3 className="text-lg font-black text-[#18216D]">No Tasks Found</h3>
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs mt-2">You're all caught up for {selectedCourseId === 'all' ? 'all subjects' : activeCourseName}!</p>
                    </div>
                )}

                {/* Modals determined by global state */}
                {submittingAssignment && (
                    <AssignmentSubmission
                        assignment={submittingAssignment}
                        existingSubmission={submittingAssignment.existing_submission}
                        onClose={() => setSubmittingAssignment(null)}
                        onSuccess={() => {
                            // Ideally refresh data here
                            setSubmittingAssignment(null);
                            // useAppState should trigger re-fetch or we manually call it, 
                            // but for now relying on AppStateContext auto-refresh might be tricky.
                            // User might need to refresh manually or we trigger a global refresh.
                            // For now, Close modal.
                        }}
                    />
                )}
                {takingQuiz && (
                    <QuizTaker
                        quiz={takingQuiz}
                        courseId={takingQuiz.courseId} // Pass courseId from our flat object
                        onClose={() => setTakingQuiz(null)}
                        onComplete={() => {
                            setTakingQuiz(null);
                        }}
                    />
                )}
            </main>
        </div>
    );
};

export default StudentGlobalTasks;
